export interface LineRange {
  start: number;
  end: number;
}

/**
 * Given per-block text outputs, build a map from blockId to the line range
 * each block occupies in the combined output (1-based line numbers).
 * Assumes blocks are joined with "\n\n" (blank line separator).
 */
export function buildBlockLineMap(
  blockOutputs: { blockId: string; text: string }[],
): Map<string, LineRange> {
  const map = new Map<string, LineRange>();
  let line = 1;
  for (let i = 0; i < blockOutputs.length; i++) {
    const { blockId, text } = blockOutputs[i];
    const lineCount = text.split("\n").length;
    map.set(blockId, { start: line, end: line + lineCount - 1 });
    line += lineCount + 1; // +1 for the blank line separator
  }
  return map;
}

/**
 * Normalize line endings so Windows CRLF (`\r\n`) from the .NET API does not
 * leave a trailing `\r` on each line. Without this, `/^% block:(.+)$/` fails
 * (`.` cannot match `\r`, and `$` without `/m` will not match before `\r`),
 * so the block↔line map stays empty and source↔canvas sync never fires.
 */
function splitLatexLines(latex: string): string[] {
  return latex.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
}

/**
 * Parse `% block:UUID` markers from LaTeX. Returns:
 * - `cleaned`: LaTeX with markers stripped
 * - `cleanLineMap`: line map keyed to cleaned output line numbers
 * - `rawLineMap`: line map keyed to original (with-markers) line numbers
 */
export function parseLatexMarkers(latex: string): {
  cleaned: string;
  cleanLineMap: Map<string, LineRange>;
  rawLineMap: Map<string, LineRange>;
} {
  const lines = splitLatexLines(latex);
  const cleanedLines: string[] = [];

  // Find marker positions and block ranges in the original
  const markers: { blockId: string; markerLine: number; originalStart: number; originalEnd: number }[] = [];
  let currentBlockId: string | null = null;
  let markerLine = 0;
  let blockStart = 0;

  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(/^% block:(.+)$/);
    if (match) {
      if (currentBlockId) {
        markers.push({ blockId: currentBlockId, markerLine, originalStart: blockStart, originalEnd: i - 1 });
      }
      currentBlockId = match[1].trim();
      markerLine = i;
      blockStart = i + 1;
    }
  }
  if (currentBlockId) {
    // Find the end of actual content — don't include \end{document} or trailing blanks
    let lastContentLine = lines.length - 1;
    for (let i = lines.length - 1; i >= blockStart; i--) {
      const trimmed = lines[i].trim();
      if (trimmed && trimmed !== "\\end{document}" && trimmed !== "\\end{thebibliography}") {
        lastContentLine = i;
        break;
      }
    }
    markers.push({ blockId: currentBlockId, markerLine, originalStart: blockStart, originalEnd: lastContentLine });
  }

  // Build cleaned output + original→clean line mapping. The `\label{blk-<id>}`
  // anchor at each block's start goes too: it is for the editor, not the reader
  // (the user, 11 Oct: "that's for debugging only"; a document id is enough to
  // trace an issue).
  const markerLineSet = new Set(markers.map((m) => m.markerLine));
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*\\label\{blk-[0-9a-fA-F-]{8,}\}\s*$/.test(lines[i])) markerLineSet.add(i);
  }
  const originalToClean = new Map<number, number>();
  for (let i = 0; i < lines.length; i++) {
    if (!markerLineSet.has(i)) {
      cleanedLines.push(lines[i]);
      originalToClean.set(i, cleanedLines.length); // 1-based
    }
  }

  // Build both line maps
  const cleanLineMap = new Map<string, LineRange>();
  const rawLineMap = new Map<string, LineRange>();

  for (const { blockId, markerLine: ml, originalStart, originalEnd } of markers) {
    // Raw map: include the marker line itself (1-based)
    rawLineMap.set(blockId, { start: ml + 1, end: originalEnd + 1 });

    // Clean map: remap to cleaned line numbers
    let cleanStart: number | undefined;
    let cleanEnd: number | undefined;
    for (let i = originalStart; i <= originalEnd; i++) {
      const cl = originalToClean.get(i);
      if (cl !== undefined) {
        if (cleanStart === undefined) cleanStart = cl;
        cleanEnd = cl;
      }
    }
    if (cleanStart !== undefined && cleanEnd !== undefined) {
      cleanLineMap.set(blockId, { start: cleanStart, end: cleanEnd });
    }
  }

  return { cleaned: cleanedLines.join("\n"), cleanLineMap, rawLineMap };
}

/**
 * Reverse lookup: given a 1-based line number, find which blockId contains it.
 */
export function lineToBlockId(
  lineNum: number,
  lineMap: Map<string, LineRange>,
): string | null {
  for (const [blockId, range] of lineMap) {
    if (lineNum >= range.start && lineNum <= range.end) {
      return blockId;
    }
  }
  return null;
}

/**
 * Collect all line numbers covered by any block in the line map.
 */
export function allMappedLines(lineMap: Map<string, LineRange>): Set<number> {
  const lines = new Set<number>();
  for (const { start, end } of lineMap.values()) {
    for (let i = start; i <= end; i++) {
      lines.add(i);
    }
  }
  return lines;
}
